'use client';

import { forwardRef, useCallback, useEffect, useImperativeHandle, useRef, useState } from 'react';
import { useTranslations } from 'next-intl';

import Modal, { type IModalRef } from '@/components/modal';
import { GoogleSVG, FacebookSVG, XSVG } from '@/components/svg';
import { IMG_HOSTING } from '@/constants/domain';
import { useFacebookFirebaseLogin } from '@/hooks/use-facebook-firebase-login';
import {
    clearLoginPending,
    markOAuthRedirectStart,
    OAUTH_RETURN_TO_LOGIN_PAGE,
} from '@/lib/auth/firebase-oauth-session';
import { signInWithGoogle } from '@/lib/firebase/auth';
import { useToast } from '@/providers/root/toast';
import { readArmsFailDetail } from '@/utils/arms/common';
import { reportLoginFail } from '@/utils/arms/login-events';
import { trackCustom, ETrackerEvents } from '@/utils/tracker';

export interface ILoginModalRef {
    open: () => void;
    close: () => void;
    showing: boolean;
}

export enum EModalType {
    ReaderLogin = 'read_login',
    PayLogin = 'pay_login',
}

const LOGIN_PRESS_FEEDBACK_MS = 150;

export interface ILoginModalProps {
    type: EModalType;
    /** Modal 关闭动画结束且已卸载展示层后触发 */
    onAfterClose?: () => void;
}

let modalApi: ILoginModalRef | null = null;
/** 关闭当前由 openLoginModal 打开的弹窗（若有） */
export function closeLoginModal() {
    modalApi?.close();
}

const LoginModal = forwardRef<ILoginModalRef, ILoginModalProps>(
    ({ type = EModalType.ReaderLogin, onAfterClose }, ref) => {
        const t = useTranslations('ProfileLogin');
        const tCommon = useTranslations('Common');
        const modalRef = useRef<IModalRef>(null);

        const { addToast } = useToast();

        const handleModalClose = useCallback(() => {
            onAfterClose?.();
        }, [onAfterClose]);

        const [googleOauthLoading, setGoogleOauthLoading] = useState(false);
        const { loginWithFacebook, isLoading: facebookOauthLoading } = useFacebookFirebaseLogin();
        const oauthLoading = googleOauthLoading || facebookOauthLoading;

        const getTriggerScene = useCallback(() => {
            if (type === EModalType.PayLogin) {
                return 'after_pay_popup';
            }
            if (type === EModalType.ReaderLogin) {
                return 'exit_reader_popup';
            }
            return '';
        }, [type]);

        const startGoogleRedirectSignIn = useCallback(async () => {
            if (oauthLoading) {
                return;
            }
            setGoogleOauthLoading(true);
            markOAuthRedirectStart(OAUTH_RETURN_TO_LOGIN_PAGE, {
                trigger_scene: getTriggerScene(),
                login_method: 'google',
            });
            try {
                await signInWithGoogle();
            } catch (err) {
                console.error('firebase sign in redirect error', err);
                clearLoginPending();
                setGoogleOauthLoading(false);
                reportLoginFail({
                    login_method: 'google',
                    trigger_scene: getTriggerScene(),
                    fail_reason: 'google_sign_in_redirect_failed',
                    fail_detail: readArmsFailDetail(err),
                });
                addToast('Login failed');
            }
        }, [getTriggerScene, oauthLoading, addToast]);

        const handleFacebookLogin = useCallback(() => {
            setTimeout(() => {
                trackCustom(ETrackerEvents.LOGIN_CLICK, { trigger_scene: getTriggerScene(), click_target: 'facebook' });

                loginWithFacebook({
                    tracking: {
                        trigger_scene: getTriggerScene(),
                        login_method: 'facebook',
                    },
                    returnTo: OAUTH_RETURN_TO_LOGIN_PAGE,
                    onSuccess: () => closeLoginModal(),
                });
            }, LOGIN_PRESS_FEEDBACK_MS);
        }, [getTriggerScene, loginWithFacebook]);

        const handleGoogleLogin = useCallback(() => {
            trackCustom(ETrackerEvents.LOGIN_CLICK, { trigger_scene: getTriggerScene(), click_target: 'google' });

            startGoogleRedirectSignIn();
        }, [startGoogleRedirectSignIn, getTriggerScene]);

        useImperativeHandle(ref, () => ({
            open: () => {
                modalRef.current?.open();
            },
            close: () => modalRef.current?.close(),
            get showing() {
                return modalRef.current?.showing ?? false;
            },
        }));

        return (
            <Modal ref={modalRef} placement='center' onClose={handleModalClose}>
                <div
                    className='relative w-[375px] px-[42px] pb-[34px] pt-[42px]'
                    style={{
                        background: `url(${IMG_HOSTING}3ef2c1bd8f11df20196ed2aac869eb2e.png)`,
                        backgroundSize: 'cover',
                    }}
                >
                    <div className='relative h-full px-5'>
                        <div className='w-full pb-3 pt-5 font-poppins-medium text-[18px] font-medium leading-6'>
                            {type === EModalType.PayLogin && (
                                <>
                                    <p className='bg-[linear-gradient(94deg,#501194_0%,#4C065A_53.58%)] bg-clip-text text-transparent'>
                                        {t('profile_login_pay_pop_up_title_dont_lose')}
                                    </p>
                                    <p className='bg-[linear-gradient(94deg,#501194_0%,#4C065A_53.58%)] bg-clip-text text-transparent'>
                                        {t('profile_login_pay_pop_up_title_your_progress')}
                                    </p>
                                </>
                            )}
                            {type === EModalType.ReaderLogin && (
                                <>
                                    <p className='bg-[linear-gradient(94deg,#501194_0%,#4C065A_53.58%)] bg-clip-text text-transparent'>
                                        {t('profile_login_reader_pop_up_title_secure')}
                                    </p>
                                    <p className='bg-[linear-gradient(94deg,#501194_0%,#4C065A_53.58%)] bg-clip-text text-transparent'>
                                        {t('profile_login_reader_pop_up_title_your_purchase')}
                                    </p>
                                </>
                            )}
                        </div>
                        <div className='py-5'>
                            {type === EModalType.PayLogin && (
                                <div className='text-center text-[14px] leading-5 text-[#888]'>
                                    {t('profile_login_pop_up_content')}
                                </div>
                            )}
                            {type === EModalType.ReaderLogin && (
                                <div className='text-center text-[12px] leading-4 text-[#888]'>
                                    {t('profile_login_reader_pop_up_content')}
                                </div>
                            )}
                            <button
                                type='button'
                                disabled={oauthLoading}
                                onClick={handleFacebookLogin}
                                className='relative mt-4 flex h-10 w-full select-none items-center justify-center rounded-[28px] bg-[#0866FF] transition-[transform,opacity] duration-150 ease-out active:scale-[0.98] active:opacity-90 disabled:opacity-60 disabled:active:scale-100 disabled:active:opacity-60'
                            >
                                <div className='flex flex-row items-center gap-[10px]'>
                                    <div className='size-[24px]'>
                                        <FacebookSVG />
                                    </div>
                                    <span className='font-poppins-medium text-[16px] font-medium leading-[22px] text-[#fff]'>
                                        {tCommon('sign_in_with_facebook')}
                                    </span>
                                </div>
                            </button>
                            <div className='relative mt-[17px] flex flex-row items-center gap-2'>
                                <div className='h-px w-full bg-[#e5e5e5]' />
                                <div className='text-[12px] leading-4 text-[#b2b2b2]'>{t('profile_login_or')}</div>
                                <div className='h-px w-full bg-[#e5e5e5]' />
                            </div>
                            <div className='mt-4 flex flex-row items-center justify-center gap-6'>
                                <button
                                    type='button'
                                    onClick={handleGoogleLogin}
                                    disabled={oauthLoading}
                                    aria-label={t('profile_login_google')}
                                    className='flex size-10 items-center justify-center rounded-full border border-[#e5e5e5]'
                                >
                                    <div className='h-[30px] w-[22px] p-[1px]'>
                                        <GoogleSVG />
                                    </div>
                                </button>
                            </div>
                        </div>
                        <button
                            type='button'
                            className='absolute -right-3 top-[-35px] p-3 text-[22px]'
                            onClick={() => {
                                modalRef.current?.close();

                                trackCustom(ETrackerEvents.LOGIN_CLICK, {
                                    trigger_scene: getTriggerScene(),
                                    click_target: 'close_btn',
                                });
                            }}
                        >
                            <XSVG />
                        </button>
                    </div>
                </div>
            </Modal>
        );
    }
);

LoginModal.displayName = 'LoginModal';

// ─── Imperative API ──────────────────────────────────────────────────────────
//
// 历史实现使用 createRoot() 把弹窗挂到 document.body 的一个游离 DOM 上，
// 这会让弹窗渲染在 Next.js / Provider 的 React 树之外，无法访问
// useRouter() 等 context（会抛错并导致 openLoginModal 看似没反应）。
//
// 现改为：在 RootProvider 中常驻挂载一个 <LoginModalHost />，通过
// 模块级 listener 把命令式调用转成宿主组件内的 state 切换。

type THostState = { type: EModalType } | null;
type THostListener = (state: THostState) => void;

let hostListener: THostListener | null = null;

function notifyHost(state: THostState) {
    hostListener?.(state);
}

export function LoginModalHost() {
    const [state, setState] = useState<THostState>(null);
    const ref = useRef<ILoginModalRef>(null);

    useEffect(() => {
        hostListener = setState;
        return () => {
            hostListener = null;
        };
    }, []);

    useEffect(() => {
        if (state) {
            modalApi = ref.current;
            ref.current?.open();
        } else {
            modalApi = null;
        }
    }, [state]);

    if (!state) {
        return null;
    }

    return <LoginModal ref={ref} type={state.type} onAfterClose={() => setState(null)} />;
}

/**
 * 打开登录弹窗（纯函数调用）。需保证应用根部已挂载 <LoginModalHost />。
 * 仅能在浏览器环境调用；若在 SSR 中调用会被忽略。
 */
export function openLoginModal({ type = EModalType.PayLogin }: { type: EModalType }) {
    if (typeof document === 'undefined') {
        return;
    }

    let triggerScene = '';
    if (type === EModalType.PayLogin) {
        triggerScene = 'after_pay_popup';
    } else if (type === EModalType.ReaderLogin) {
        triggerScene = 'exit_reader_popup';
    }

    trackCustom(ETrackerEvents.LOGIN_SHOW, { trigger_scene: triggerScene });

    notifyHost({ type });
}

export default LoginModal;
